import React from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Badge } from '@freelancelabs/components';

import { ListSkeleton } from '../../../components/Skeletons';
import { DataScope } from '../../../graphql/__generated__/schema';
import { AccountActivitiesDocument } from '../../../graphql/__generated__/types';
import { dataScopeLabel } from '../display';
import { DraftAttachment } from './draft';

interface Props {
    attachment: DraftAttachment;
    accountName: string;
    roleName: string;
    /** L'adresse, pour relire la MEME liste que l'etape 2 et nommer ce qui est coche. */
    email: string;
    /** Pourquoi ce rôle n'est plus accordable, ou null. Voir le rendu ci-dessous. */
    refusal?: string | null;
}

/**
 * One line of the recap: an account, a role, and, when the perimeter is restricted
 *: **the markets by name**.
 *
 * Naming them is the third condition the pre-ticking rests on. The administrator
 * validates a screen, not each box: « Restreint à 3 marchés » is a count of
 * something they can no longer see, and a perimeter that arrived pre-ticked would
 * leave here unread. So the last screen before the invitation says which ones, and
 * which of them the query proposed.
 *
 * La liste vient de la MEME lecture qu'à l'étape 2, `accountActivities`, donc Apollo la
 * ressert de son cache quand l'étape vient d'être vue.
 * the recap and the selector read alike.
 */
const ReviewAttachment = ({ attachment, accountName, roleName, refusal, email }: Props) => {
    const restricted = attachment.dataScope === DataScope.Restricted;

    /**
     * LA MEME LISTE QU'A L'ETAPE 2, sans recherche.
     *
     * Le brouillon ne porte que des IDENTIFIANTS : nommer ce qui est coche demande de relire
     * la liste. Apollo la ressert de son cache quand l'etape 2 vient d'etre vue, donc ce
     * n'est un aller-retour que si l'operateur arrive ici autrement.
     *
     * Attention, SANS `search` : le recapitulatif doit nommer TOUT ce qui est accorde, y
     * compris ce qu'une recherche laissee en place cachait a l'ecran precedent. C'est
     * exactement l'erreur que cet ecran existe pour empecher.
     */
    const { data, loading } = useQuery(AccountActivitiesDocument, {
        variables: { organizationId: attachment.organizationId, email, search: '' },
        skip: !restricted,
    });
    const activities = data?.accountActivities ?? null;

    /**
     * Ce qui est accorde, nomme, et **jamais moins de lignes que d'identifiants** : une
     * activite dont le nom n'a pas resolu s'imprime en identifiant opaque. Un recapitulatif
     * qui laisserait tomber une ligne en silence sous-estimerait ce qu'on s'apprete a
     * accorder, ce qui est la seule erreur que cet ecran existe pour empecher.
     */
    const byId = new Map<string, string | null>();
    for (const need of activities?.needs ?? []) {
        byId.set(need.id as string, need.name ?? null);
    }
    for (const group of activities?.marketGroups ?? []) {
        for (const service of group.services) {
            byId.set(service.id as string, service.name ?? null);
        }
    }
    const granted = [
        ...attachment.needIds.map((id) => ({ id, kind: 'need' as const })),
        ...attachment.serviceIds.map((id) => ({ id, kind: 'service' as const })),
    ];

    // Garanti d'etre celui de l'adresse courante, ou absent : `applyPatch` retire
    // l'instantane des que l'adresse pour laquelle il a ete calcule cesse d'etre la bonne.
    const preTicked = new Set([
        ...(attachment.suggestion?.needIds ?? []),
        ...(attachment.suggestion?.serviceIds ?? []),
    ]);
    const matchedContactCount = attachment.suggestion?.matchedContactCount ?? 0;

    return (
        <li className="flex flex-col gap-1 border-t border-border-norm pt-2 text-sm first:border-0 first:pt-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <b className="text-text-900">{accountName}</b>
                <span className="text-text-600">{roleName}</span>
                <span className="text-text-600">
                    {dataScopeLabel(attachment.dataScope, attachment.needIds.length + attachment.serviceIds.length)}
                </span>
                {/* Le rôle reste nommé, et le refus se pose à côté plutôt qu'à sa
                    place : l'opérateur doit voir ce qu'il avait choisi pour savoir
                    quoi choisir à la place. */}
                {refusal && (
                    <span className="text-danger-600" role="alert">
                        {c('Onboarding recap').t`Role no longer grantable by you — ${refusal}`}
                    </span>
                )}
            </div>

            {/* Said again here, and not only at step 2: a shared mailbox is the case
                where the pre-ticking granted one person's markets on another's
                business, and this is the screen where it is validated. */}
            {restricted && matchedContactCount > 1 && (
                <p className="text-warning-700" role="alert">
                    {c('Onboarding recap')
                        .t`This address matches ${matchedContactCount} contacts — a shared mailbox. Some of the markets below were pre-ticked from another person's business.`}
                </p>
            )}

            {restricted &&
                (loading && !activities ? (
                    <ListSkeleton rows={4} />
                ) : (
                    <ul className="flex flex-col gap-1">
                        {granted.map(({ id, kind }) => (
                            <li key={id} className="flex flex-wrap items-center gap-2 text-text-600">
                                <span>{byId.get(id) ?? id}</span>
                                {/* Le TYPE est dit : un besoin et une prestation portent des noms qui
                                    se ressemblent, et le récapitulatif est le dernier endroit où
                                    l'opérateur peut s'apercevoir qu'il a coché l'un pour l'autre. */}
                                <Badge category="default" size="small">
                                    {kind === 'need' ? c('Onboarding recap').t`Need` : c('Onboarding recap').t`Service`}
                                </Badge>
                                {/* Ce que personne n'a coché à la main, et sur quel fondement : le
                                    MÊME mot qu'à l'étape 2, pour que les deux écrans ne décrivent pas
                                    un seul fait de deux façons. */}
                                {preTicked.has(id) && (
                                    <Badge category="info" size="small">
                                        {c('Onboarding recap').t`Named by the source`}
                                    </Badge>
                                )}
                            </li>
                        ))}
                    </ul>
                ))}
        </li>
    );
};

export default ReviewAttachment;
